import { ReactElement, useEffect, useRef } from 'react'
import CallMerge from '@mui/icons-material/CallMerge'
import Grid4x4Icon from '@mui/icons-material/Grid4x4'
import { Box, Stack } from '@mui/material'
import { ToggleButton, ToggleButtonGroup } from '@ui/actions'
import { VerticalDivider } from '@ui/structure'
import { useControlStore } from '@editorStores'
import { ProblemControls } from '@editorComponents'
import ClipPlaneControls from './ClipPlaneControls'
import ComponentDetailControls from './ComponentDetailControls'
import DrawControls from './DrawControls'
import FilterViewSelector from './FilterViewSelector'
import GhostModeControls from './GhostModeControls'
import ModelScreenshotWidget from './ModelScreenshotWidget'
import SelectionControls from './SelectionControls'
import TapelineControls from './TapelineControls'
import { ToggleShortcutInfoButton } from './ToggleShortcutInfoButton'
import VisibilitySelector from './VisibilitySelector'

interface Props {
  selectionControls?: boolean
}

const ControlsLeft = ({ selectionControls = false }: Props): ReactElement => {
  const orthographicDrawMode = useControlStore(state => state.orthographicDrawMode)

  return (
    <Stack direction="row" spacing={0.75} divider={<VerticalDivider />}>
      <TapelineControls />

      <ClipPlaneControls />

      <DrawControls />

      <VisibilitySelector />

      <GhostModeControls />

      <FilterViewSelector />

      {!orthographicDrawMode && selectionControls && <SelectionControls />}

      <ComponentDetailControls />
    </Stack>
  )
}

const ControlsRight = (): ReactElement => {
  const widgetPortalRef = useRef<HTMLDivElement | null>(null)

  const { gridEnabled, toggleGridEnabled, axesEnabled, toggleAxesEnabled } = useControlStore(
    state => ({
      gridEnabled: state.gridEnabled,
      toggleGridEnabled: state.toggleGridEnabled,
      axesEnabled: state.axesEnabled,
      toggleAxesEnabled: state.toggleAxesEnabled,
      hiddenElementIds: state.hiddenElementIds,
    }),
  )
  const setWidgetPortalElement = useControlStore(state => state.setWidgetPortalElement)

  useEffect(() => {
    if (!widgetPortalRef.current) return

    setWidgetPortalElement(widgetPortalRef.current)
  }, [setWidgetPortalElement])

  return (
    <Stack direction="row" spacing={0.75}>
      {
        <>
          <ToggleButtonGroup>
            <ToggleShortcutInfoButton />
          </ToggleButtonGroup>
        </>
      }

      <VerticalDivider />

      <ToggleButtonGroup>
        <ToggleButton
          hint="Raster anzeigen"
          onClick={toggleGridEnabled}
          selected={gridEnabled}
          Icon={Grid4x4Icon}
        />
        <ToggleButton
          hint="Koordinatensystem anzeigen"
          onClick={toggleAxesEnabled}
          selected={axesEnabled}
          Icon={CallMerge}
        />
      </ToggleButtonGroup>

      <VerticalDivider />

      <ModelScreenshotWidget />

      <Box ref={widgetPortalRef} display="flex" flex={1} />

      <VerticalDivider />

      <ProblemControls />

      <VerticalDivider />
    </Stack>
  )
}

export { ControlsLeft, ControlsRight }
